import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import type {
  IProduct,
  IProductComplete,
} from "../interfaces/Products/IProductDetail";
import { convertYearMonthyWeek } from "../utils/date";
import { EDiary, EIsBackTest, EPeriod } from "../interfaces/Filters";

export interface IParamsPerformance {
  productId?: number[];
  symbols?: string[];
  is_backtest?: EIsBackTest;
  diary?: EDiary;
  period?: EPeriod;
  close_date_init?: Date;
  close_date_end?: Date;
}

const getProduct = async (
  { productId }: IParamsPerformance,
  signal?: AbortSignal
): Promise<IProductComplete> => {
  const { data } = await api.get<IProduct>(`web/product/${productId}`, {
    signal,
  });

  return {
    organization: data.organizations,
    plan: data.plans,
    product: data,
  };
};

export const useProduct = (params: IParamsPerformance) => {
  const { productId } = params;
  return useQuery({
    queryKey: ["Product", productId],
    queryFn: () => getProduct(params),
    retry: 0,
  });
};

export interface IResponsePerformance {
  data: {
    date: string;
    value: number;
    value_text: string;
    value_acumulated: number;
    value_acumulated_text: string;
    percentage: number;
    percentage_text: string;
    percentage_acumulated: number;
    percentage_acumulated_text: string;
  }[];
  backtest_date: string;
  available_symbols: string[];
  assertivity: string;
  avg_daily: string;
  max_gain: string;
  max_loss: string;
  profitability: string;
  qty_days: string;
  qty_gain_days: string;
  qty_loss_days: string;
  total: string;
  total_gain: string;
  total_loss: string;
  total_acumulated: string;
}

const getProductPerformance = async (
  {
    productId,
    symbols,
    is_backtest,
    close_date_init,
    close_date_end,
  }: IParamsPerformance,
  signal?: AbortSignal
) => {
  const params = new URLSearchParams();
  if (!!symbols && symbols.length > 0) {
    symbols.forEach((symbol) =>
      !!symbol ? params.append("symbol", symbol.toString()) : null
    );
  }

  const convertCloseDateInit = convertYearMonthyWeek(close_date_init);
  const convertCloseDateEnd = convertYearMonthyWeek(close_date_end);
  if (!!is_backtest) params.append("is_backtest", is_backtest);
  if (!!convertCloseDateInit)
    params.append("close_date_init", convertCloseDateInit);
  if (!!convertCloseDateEnd)
    params.append("close_date_end", convertCloseDateEnd);

  const { data } = await api.get<IResponsePerformance>(
    `web/product/${productId}/performance`,
    {
      params,
      signal,
    }
  );

  return data;
};

export const useProductPerformance = (params: IParamsPerformance) => {
  return useQuery({
    queryKey: ["ProductPerformance", params],
    queryFn: () => getProductPerformance(params),
    retry: 0,
  });
};

// Hook para analista - endpoint específico
const getAnalystProductPerformance = async (
  {
    productId,
    symbols,
    is_backtest,
    close_date_init,
    close_date_end,
  }: IParamsPerformance,
  signal?: AbortSignal
) => {
  const params = new URLSearchParams();
  if (!!symbols && symbols.length > 0) {
    symbols.forEach((symbol) =>
      !!symbol ? params.append("symbol", symbol.toString()) : null
    );
  }

  const convertCloseDateInit = convertYearMonthyWeek(close_date_init);
  const convertCloseDateEnd = convertYearMonthyWeek(close_date_end);
  if (!!is_backtest) params.append("is_backtest", is_backtest);
  if (!!convertCloseDateInit)
    params.append("close_date_init", convertCloseDateInit);
  if (!!convertCloseDateEnd)
    params.append("close_date_end", convertCloseDateEnd);

  const { data } = await api.get<IResponsePerformance>(
    `web/positions/analyst/product-performance/${productId}`,
    {
      params,
      signal,
    }
  );

  return data;
};

export const useAnalystProductPerformance = (params: IParamsPerformance) => {
  return useQuery({
    queryKey: ["AnalystProductPerformance", params],
    queryFn: () => getAnalystProductPerformance(params),
    retry: 0,
    enabled: !!params.productId && params.productId.length > 0,
  });
};
